<template>
  <div v-if="baseData" class="base-report report flex flex-column">
    <div class="report-title" data-html2canvas-ignore>
      <span>Общая аналитика для одной группы респондентов</span>
      <a-button @click="print" class="secondary-button download-report" data-html2canvas-ignore>
        <template #icon>
          <DownloadOutlined />
        </template>
        Скачать pdf
      </a-button>
    </div>
    <consultation />
    <a-divider />

    <div class="main_report_wrapper" id="print_element">
      <div class="report-block">
        <div class="report-title lvl-2">
          1. Средние показатели по рассматриваемой группе
        </div>
        <div v-html="descriptions.average" class="report-description"></div>
        <div class="report-chart">
          <div class="flex flex-wrap justify-content-center report-chart-arrows">
            <div v-if="hasTotal" class="w-full scale">
              <div class="text-center mb-6">
                <strong>Средние показатели по общему эмоциональному интеллекту</strong>
              </div>
              <visual-analog-scale :value="baseData.average['total']" :scaleLabel="dictionary['total']"/>
              <hr class="my-4 border-none h-px bg-gray-300">
            </div>

            <div v-if="hasDomains" class="w-full scale">
              <div class="text-center mt-12 mb-6">
                <strong>Средние показатели по двум доменам эмоционального интеллекта</strong>
              </div>
              <div class="flex flex-wrap justify-content-center">
                <visual-analog-scale :value="baseData.average['domain_1']" :scaleLabel="dictionary['domain_1']"/>
                <visual-analog-scale :value="baseData.average['domain_2']" :scaleLabel="dictionary['domain_2']"/>
              </div>
              <hr class="my-4 border-none h-px bg-gray-300">
            </div>

            <div v-if="hasBranches" class="w-full scale">
              <div class="text-center mt-12 mb-6">
                <strong>Средние показатели по четырем ветвям эмоционального интеллекта</strong>
              </div>
              <div class="flex flex-wrap justify-content-center">
                <visual-analog-scale
                    v-for="branch in branches"
                    :key="branch"
                    :value="baseData.average[branch]"
                    :scaleLabel="dictionary[branch]"
                />
              </div>
            </div>
          </div>
          <div data-html2canvas-ignore>
            <consultation />
          </div>
        </div>
      </div>
      <div class="report-block">
        <div class="report-title lvl-2">2. Распределение респондентов по уровню развития общего ЭИ</div>
        <div
          class="
            pie-chart__content
            report-content
            flex flex-wrap
            justify-content-center
          "
        >
          <div v-html="descriptions.total" class="report-description"></div>
          <div class="report-chart">
            <bar-chart
                :chartLabels="levelDictionary"
                :chartData="barTotalData"
            />
            <div class="chart-legend" style="text-align: center;">
              Общий ЭИ
            </div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div class="report-block two_domains_analytics">
        <div class="report-title lvl-2">3. Распределение респондентов по двум доменам ЭИ</div>
        <div class="report-content flex flex-wrap justify-content-center">
          <div v-html="descriptions.domains" class="report-description"></div>
          <div class="report-chart">
            <div class="flex flex-wrap justify-content-center">
              <div style="width: 50%" v-for="(domain, indexDomain) in barDomainsData" :key="indexDomain">
                <bar-chart
                    :chartLabels="levelDictionary"
                    :chartData="[domain]"
                    :htmlLegend="'base-bar-domains-' + indexDomain"
                    :full-width="true"
                />
                <div class="chart-legend" :id="'base-bar-domains-' + indexDomain"></div>
              </div>
            </div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div class="report-block respondent_ratio_block">
        <div class="report-title lvl-2">4. Соотношение респондентов по двум доменам ЭИ</div>
        <div class="report-content flex flex-wrap justify-content-center">
          <div
            v-html="descriptions.dominateDomains"
            class="report-description"
          ></div>
          <div class="report-chart doughnut-chart">
            <div class="flex flex-wrap justify-content-center items-center">
              <div class="large-chart-wrapper">
                <doughnut-chart
                    :chartLabels="[
                      'Опытный домен выше стратегического',
                      'Стратегический домен выше опытного',
                      'Нет доминирующего домена'
                    ]"
                    :chartData="[
                      baseData.dominateDomains[0].count_dominate_domain_1,
                      baseData.dominateDomains[0].count_dominate_domain_2,
                      baseData.dominateDomains[0].count_without_dominate_domain
                    ]"
                    htmlLegend="base-doughnut-dominate-domains"
                    :is-full-width="true"
                />
              </div>

              <div class="small-charts-wrapper">
                <div class="small-chart-block">
                  <doughnut-chart
                      :chartLabels="[
                        'Опытный домен выше стратегического',
                        'Стратегический домен выше опытного',
                        'Нет доминирующего домена'
                      ]"
                      :chartData="[
                        baseData.dominateDomains[0].count_dominate_domain_1,
                        baseData.dominateDomains[0].count_dominate_domain_2,
                        baseData.dominateDomains[0].count_without_dominate_domain
                      ]"
                      :background-color="['#C0FF72', '#CCCCCC', '#CCCCCC']"
                      :is-full-width="isSmallScreen"
                  />
                  <div class="chart-description">
                    В группе, где доминирует опытный домен, разрыв между средними показателями доменов составляет
                    {{
                      getDifference('dominate_domain_1_average', 'domain_1', 'domain_2')
                    }} {{
                      getWordFormBasedOnTheCount(getDifference('dominate_domain_1_average', 'domain_1', 'domain_2'), 'балл', 'балла', 'баллов')
                    }}.
                  </div>
                </div>

                <div class="small-chart-block second-block">
                  <doughnut-chart
                      :chartLabels="[
                        'Опытный домен выше стратегического',
                        'Стратегический домен выше опытного',
                        'Нет доминирующего домена'
                      ]"
                      :chartData="[
                        baseData.dominateDomains[0].count_dominate_domain_1,
                        baseData.dominateDomains[0].count_dominate_domain_2,
                        baseData.dominateDomains[0].count_without_dominate_domain
                      ]"
                      :background-color="['#CCCCCC', '#CB6BE6', '#CCCCCC']"
                      :is-full-width="isSmallScreen"
                  />
                  <div class="chart-description">
                    В группе, где доминирует стратегический домен, разрыв между средними показателями доменов составляет
                    {{
                      getDifference('dominate_domain_2_average', 'domain_2', 'domain_1')
                    }} {{
                      getWordFormBasedOnTheCount(getDifference('dominate_domain_2_average', 'domain_2', 'domain_1'), 'балл', 'балла', 'баллов')
                    }}.
                  </div>
                </div>
              </div>
            </div>
            <div class="chart-legend" id="base-doughnut-dominate-domains"></div>
          </div>
          <div class="report-chart">
            <div>
              Чем меньше разрыв показателей между доменами, тем более гармонично и слажено работают способности обоих доменов.
              Чем больше разрыв между показателями доменов, тем менее согласованно взаимодействуют способности обоих доменов между собой,
              что ведет к перекосам и снижению эффективности
            </div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div class="report-block respondent_distribution_block">
        <div class="report-title lvl-2">5. Распределение респондентов по уровню развития по каждой ветви ЭИ</div>
        <div class="report-content flex flex-wrap justify-content-center">
          <div v-html="descriptions.branches" class="report-description"></div>
          <div class="report-chart">
            <div class="flex flex-wrap justify-content-center">
              <div style="width: 50%" v-for="(branch, indexBranch) in branchBarStatistic" :key="indexBranch">
                <bar-chart
                    :chartLabels="levelDictionary"
                    :chartData="[branch]"
                    :htmlLegend="'base-bar-branch-statistic-' + indexBranch"
                    :full-width="true"
                />
                <div class="chart-legend" :id="'base-bar-branch-statistic-' + indexBranch"></div>
              </div>
            </div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div class="report-block strong_side_block">
        <div class="report-title lvl-2 ratio_title">6. Сильные стороны</div>
        <div class="report-content flex flex-wrap justify-content-center">
          <div
            v-html="descriptions.dominateBranches"
            class="report-description strong_side_description"
          ></div>
          <div class="report-chart scale">
            <visual-analog-scale
                v-for="(value, key, index) in getFilteredBranches(sortedBranches(baseData.dominateBranches.average))"
                :key="key"
                :value="Math.round(Object.values(value)[0])"
                :scaleLabel="dominateLabels[index]"
                :highlightedGreen="Object.values(value)[0] === maxBranchValue"
            />
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
          <div class="report-chart scale">
            <div class="flex flex-wrap justify-content-center">
              <div style="width: 50%" v-for="(branch, indexBranch) in dominateBarRespondentsData" :key="indexBranch">
                <bar-chart
                    :chartLabels="levelDictionary"
                    :chartData="[branch]"
                    :htmlLegend="'base-bar-dominate-branches-respondents-' + indexBranch"
                    :full-width="true"
                />
                <div class="chart-legend" :id="'base-bar-dominate-branches-respondents-' + indexBranch"></div>
              </div>
              <div
                  class="chart-legend"
                  id="base-bar-dominate-branches-respondents"
              ></div>
            </div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div class="report-block potential_grow_wrapper">
        <div class="report-title lvl-2 grow_title">7. Зоны потенциального роста</div>
        <div class="report-content flex flex-wrap justify-content-center">
          <div
            v-html="descriptions.growingBranches"
            class="report-description potential_grow_description"
          ></div>
          <div class="report-chart scale grow_scale">
            <visual-analog-scale
                v-for="(value, key, index) in getFilteredBranches(sortedBranches(baseData.growingBranches.average))"
                :key="key"
                :value="Math.round(Object.values(value)[0])"
                :scaleLabel="growLabels[index]"
                :highlightedRed="Object.values(value)[0] === minBranchValue"
            />
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
          <div class="report-chart scale">
            <div class="flex flex-wrap justify-content-center">
              <div style="width: 50%" v-for="(branch, indexBranch) in growingBarRespondentsData" :key="indexBranch">
                <bar-chart
                    :chartLabels="levelDictionary"
                    :chartData="[branch]"
                    :htmlLegend="'base-bar-growing-branches-respondents-' + indexBranch"
                    :full-width="true"
                />
                <div class="chart-legend" :id="'base-bar-growing-branches-respondents-' + indexBranch"></div>
              </div>
            </div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div v-if="baseData.harmonious.count.harmonious" class="report-block">
        <div class="report-title lvl-2 harmony_title">8. Гармоничные профили</div>
        <div class="report-content flex flex-wrap justify-content-center">
          <div v-html="descriptions.harmonious" class="report-description harmony_description"></div>
          <div class="report-chart">
            <doughnut-chart
              :chartLabels="['Гармонические профили', 'Негармонические профили']"
              :chartData="[
                baseData.harmonious.count.harmonious,
                baseData.harmonious.count.inharmonious,
              ]"
              htmlLegend="base-pie-harmonic"
            />
            <div class="chart-legend" id="base-pie-harmonic"></div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
          <div class="report-chart">
            <bar-chart
              :chartLabels="levelDictionary"
              :chartData="harmonicBarData"
              htmlLegend="base-bar-harmonic"
            />
            <div class="chart-legend" id="base-bar-harmonic"></div>
            <div data-html2canvas-ignore>
              <consultation />
            </div>
          </div>
        </div>
      </div>
      <div v-if="baseData" style="width: 100%">
        <div v-if="getComputedData.domainLabel !== 'Сбалансированный'">
          Таким образом, у данной группы среднее значение по общему ЭИ относится к уровню <b>{{ getComputedData.totalLevel }}</b>.
          В группе наиболее развит <b>{{ getComputedData.domainLabel }}</b> домен, который находится на уровне <b>{{ getComputedData.domainLevel }}</b>.
          Сильной стороной группы является ветвь <b>{{ getComputedData.dominateBranchLabel }}</b>, которая относится к уровню <b>{{ getComputedData.dominateBranchLevel }}</b>.
          В зону потенциального роста входит ветвь <b>{{ getComputedData.growingBranchLabel }}</b>, которая относится к уровню <b>{{ getComputedData.growingBranchLevel }}</b>.
          Хотите узнать, как повысить показатели - обращайтесь к консультанту.
        </div>
        <div v-else >
          Таким образом, у данной группы среднее значение по общему ЭИ относится к уровню <b>{{ getComputedData.totalLevel }}</b>.
          В группе респондентов домены развиты на одинаково и относятся к уровню <b>{{ getComputedData.domainLevel }}</b>.
          Сильной стороной группы является ветвь <b>{{ getComputedData.dominateBranchLabel }}</b>, которая относится к уровню <b>{{ getComputedData.dominateBranchLevel }}</b>.
          В зону потенциального роста входит ветвь <b>{{ getComputedData.growingBranchLabel }}</b>, которая относится к уровню <b>{{ getComputedData.growingBranchLevel }}</b>.
          Хотите узнать, как повысить показатели - обращайтесь к консультанту.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {computed, ref} from "@vue/runtime-core";
import ArrowChart from "../charts/ArrowChart.vue";
import { useStore } from "vuex";
import PieChart from "../charts/PieChart.vue";
import BarChart from "../charts/BarChart.vue";
import DoughnutChart from "../charts/DoughnutChart.vue";
import useAnalyticsFunctions from "./../mixins/useAnalyticsFunctions";
import Consultation from "./../charts/details/Consultation.vue";
import VisualAnalogScale from "@/feature/analytics/charts/VisualAnalogScale.vue";
import {onMounted, onUnmounted} from "vue";
import {getWordFormBasedOnTheCount} from "@/views/Report/utils";
import html2pdf from "html2pdf.js";
import {DownloadOutlined} from "@ant-design/icons-vue";
export default {
  methods: {getWordFormBasedOnTheCount},
  components: {DownloadOutlined, VisualAnalogScale, ArrowChart, PieChart, BarChart, DoughnutChart, Consultation },
  name: "base-report",
  setup() {
    const store = useStore();
    const {
      keysToLabels,
      barDataForAreas,
      barRespondentsData,
      checkScores,
      dictionary,
      levelDictionary,
      branchesDictionary,
    } = useAnalyticsFunctions();
    const baseData = computed(() => store.state.analytics.reportData),
      descriptions = computed(() => baseData.value.block_descriptions),
      barTotalColors = ['#FF3131', '#FF65C3', '#8C52FF', '#004AAD', '#00BF62'],
      barTotalData = computed(() => {
        let newData = [
          {
            data: [],
            label: 'Общий ЭИ',
            color: barTotalColors
          }
        ];
        const total = baseData.value.total[0];
        checkScores(newData[0].data, total);
        return newData;
      }),
      barDomainsData = computed(() => {
        let newData = [
          {
            data: [],
            label: "Опытный домен",
            color: '#C0FF72'
          },
          {
            data: [],
            label: "Стратегический домен",
            color: '#CB6BE6'
          },
        ];
        checkScores(newData[0].data, baseData.value.domains.domain_1);
        checkScores(newData[1].data, baseData.value.domains.domain_2);
        return newData;
      }),
      barDomaintDomains = computed(() => {
        let newData = [];
        newData.push({
          label: "Опытный домен, среднее",
          data: [
            baseData.value.dominateDomains[1].dominate_domain_1_average
              .domain_1,
            baseData.value.dominateDomains[1].dominate_domain_2_average
              .domain_1,
          ],
        });
        newData.push({
          label: "Стратегический домен, среднее",
          data: [
            baseData.value.dominateDomains[1].dominate_domain_1_average
              .domain_2,
            baseData.value.dominateDomains[1].dominate_domain_2_average
              .domain_2,
          ],
        });
        return newData;
      }),
      branchBarStatistic = computed(() => {
        let newData = [];
        const colors = ['#7ED956', '#FFDE59', '#CC00CC', '#5271FF'];
        let index = 0;

        for (let key in baseData.value.branches) {
          newData.push({ label: dictionary[key], data: [], color: colors[index % colors.length] });
          checkScores(
            newData[newData.length - 1].data,
            baseData.value.branches[key]
          );
          index++;
        }
        return newData;
      }),
      dominateLabels = computed(() =>
        keysToLabels(Object.keys(sortedBranches(baseData.value.dominateBranches.counts)))
      ),
      growLabels = computed(() =>
          keysToLabels(Object.keys(sortedBranches(baseData.value.growingBranches.counts)))
      ),
      dominatePieData = computed(() =>
        pieDataForAreas(baseData.value.dominateBranches.counts)
      ),
      growingPieData = computed(() =>
        Object.values(baseData.value.growingBranches.counts)
      ),
      dominateBarData = computed(() =>
        barDataForAreas(baseData.value.dominateBranches.average)
      ),
      growingBarData = computed(() =>
        barDataForAreas(baseData.value.growingBranches.average)
      ),
      dominateBarRespondentsData = computed(() => {
        const colors = ['#7ED956', '#FFDE59', '#CC00CC', '#5271FF'];
         return barRespondentsData(
            baseData.value.dominateBranches.respondentsDistributionByLevels
        ).map((item, index) => ({
           ...item,
           color: colors[index % colors.length]
         }))
      }),
      segments = [
        { from: 50, to: 70, label: 'Элементарный' },
        { from: 70, to: 90, label: 'Начальный' },
        { from: 90, to: 110, label: 'Средний' },
        { from: 110, to: 130, label: 'Высокий' },
        { from: 130, to: 150, label: 'Высший' },
      ],
      getLevel = (value, segments) => {
        const segment = segments.find(segment => value >= segment.from && value < segment.to);
        return segment ? segment.label : 'НЕИЗВЕСТНО';
      },
      getMaxKey = (data) => {
        return Object.entries(data)
            .filter(([key, value]) => value !== undefined && typeof value === 'number')
            .reduce((max, [key, value]) => (value > max.value ? { key, value } : max), { key: null, value: -Infinity }).key;
      },
      getComputedData = computed(() => {
        const growingBranches = baseData.value.growingBranches?.counts || {};
        const dominateBranches = baseData.value.dominateBranches?.counts || {};
        const averages = baseData.value.average || {};

        const totalLevel = getLevel(averages['total'], segments);

        const domainLabel = averages['domain_1'] > averages['domain_2']
            ? 'Опытный'
            : averages['domain_1'] < averages['domain_2']
                ? 'Стратегический'
                : 'Сбалансированный';

        const domainKey = averages['domain_1'] >= averages['domain_2'] ? 'domain_1' : 'domain_2';
        const domainLevel = getLevel(averages[domainKey], segments);

        const dominateBranchKey = getMaxKey(dominateBranches);
        const dominateBranchLabel = branchesDictionary[dominateBranchKey] || null;

        const averageDominateBranch = dominateBranchKey ? baseData.value.dominateBranches?.average[dominateBranchKey][dominateBranchKey]: null;
        const dominateBranchLevel = averageDominateBranch ? getLevel(averageDominateBranch, segments) : null;

        const growingBranchKey = getMaxKey(growingBranches);
        const growingBranchLabel = branchesDictionary[growingBranchKey] || null;

        const averageGrowingBranch = growingBranchKey
            ? baseData.value.growingBranches?.average[growingBranchKey][growingBranchKey]
            : null;
        const growingBranchLevel = averageGrowingBranch ? getLevel(averageGrowingBranch, segments) : null;

        return {
          totalLevel,
          domainLabel,
          domainLevel,
          growingBranchLabel,
          growingBranchLevel,
          dominateBranchLabel,
          dominateBranchLevel
        };
      }),
      growingBarRespondentsData = computed(() => {
        const colors = ['#7ED956', '#5271FF', '#FFDE59', '#CC00CC'];

        let newData =  barRespondentsData(
            baseData.value.growingBranches.respondentsDistributionByLevels
        )

        return newData.map((item, index) => ({
          ...item,
          color: colors[index % colors.length],
        }));
      }),
      harmonicBarData = computed(() => {
        let newData = [
          {
            data: [],
            label: "Гармонические профили",
          },
        ];
        checkScores(newData[0].data, baseData.value.harmonious.levels);
        return newData;
      });

    const colors = {
      branch_1: '#7ED956', //Распознавание эмоций
      branch_2: '#FFDE59', //Использование эмоций
      branch_3: '#5271FF', //Анализ эмоций
      branch_4: '#CC00CC', //Управление эмоциями
    }

    const pieDataForAreas = (average) => {
      let newData = [];
      let maxLength = 1;
      const averageBranches = average;
      for (let branch in averageBranches) {
        Object.values(branch).length > 8 ? maxLength = 2 : ""
      }

      for (let i = 1; i <= maxLength; i++) {
        newData.push({ label: "", data: [], color: []});
        for (let branch in averageBranches) {

          let keys =[]
          keys.push(branch.split(','))

            if (Object.values(branch).length > 8) {
              let items = branch.split(',')
              const colorsNames = colors[items[0]] + ',' + colors[items[1]]
              newData[i - 1].data.push(averageBranches[branch])
              newData[i - 1].color.push(colorsNames)
            }

          else {
            newData[i - 1].data.push(averageBranches[branch])
            newData[i - 1].color.push(colors[keys[0][i-1]])
          }
        }
      }
      return newData;
    };

    const print = () => {
      let block = document.querySelector('.two_domains_analytics');
      block.style.marginTop = '380px';

      let distBlock = document.querySelector('.respondent_distribution_block');
      distBlock.style.marginTop = '50px';

      let respondentRatio = document.querySelector('.respondent_ratio_block');
      respondentRatio.style.marginBottom = '0';

      let ratioTitle = document.querySelector('.ratio_title');
      ratioTitle.style.marginTop = '10px';
      ratioTitle.style.marginBottom = '0';

      let strongSideBlock = document.querySelector('.strong_side_block');
      strongSideBlock.style.marginBottom = '0';

      let strongSideDesc = document.querySelector('.strong_side_description');
      strongSideDesc.style.marginBottom = '0';
      strongSideDesc.style.fontSize = '14px';

      let growTitle = document.querySelector('.grow_title');
      growTitle.style.marginTop = '0';
      growTitle.style.marginBottom = '0';
      growTitle.style.paddingBottom = '0';

      let growDesc = document.querySelector('.potential_grow_description');
      growDesc.style.marginBottom = '0';
      growDesc.style.fontSize = '14px';

      let growScale = document.querySelector('.grow_scale');
      growScale.style.marginBottom = '0';

      let potentialWrapper = document.querySelector('.potential_grow_wrapper');
      potentialWrapper.style.marginBottom = '0';

      let harmonyTitle = document.querySelector('.harmony_title');
      let harmonyDesc = document.querySelector('.harmony_description');

      if (harmonyTitle) {
        harmonyTitle.style.marginTop = '0';
        harmonyTitle.style.marginBottom = '0';
        harmonyTitle.style.paddingBottom = '0';

        harmonyDesc.style.marginBottom = '5px';
      }

      html2pdf(document.getElementById("print_element"), {
        autoPaging: 'text',
        margin: [12, 8, 15, 8],
        filename: "analytics_base" + Date.now() + ".pdf",
        html2canvas: {
          dpi: 192,
          scale: 4,
          windowWidth: 1090,
        },
        pagebreak: {mode: 'css'},

      }).then(() => {
        block.style.marginTop = '0';
        distBlock.style.marginTop = '0';
        respondentRatio.style.marginBottom = '20px';
        strongSideBlock.style.marginBottom = '20px';
        ratioTitle.style.marginTop = '20px';
        ratioTitle.style.marginBottom = '20px';

        growTitle.style.marginTop = '20px'
        growTitle.style.marginBottom = '20px';
        growTitle.style.paddingBottom = '20px';

        growScale.style.marginBottom = '20px';
        strongSideDesc.style.marginBottom = '25px';
        strongSideDesc.style.fontSize = '16px';
        growDesc.style.marginBottom = '25px';
        growDesc.style.fontSize = '16px';

        if (harmonyTitle) {
          harmonyTitle.style.marginTop = '20px'
          harmonyTitle.style.marginBottom = '20px';
          harmonyTitle.style.paddingBottom = '20px';

          harmonyDesc.style.marginBottom = '20px';
        }
      });
    };

    const getDifference = (groupKey, keyA, keyB) => {
      const group = baseData.value.dominateDomains[1][groupKey];
      return Math.round(group[keyA]) - Math.round(group[keyB]);
    };

    const maxBranchValue = computed(() =>
      Math.max(...Object.values(baseData.value.dominateBranches.average).map(branch => Object.values(branch)[0]))
    );

    const minBranchValue = computed(() =>
        Math.min(...Object.values(baseData.value.growingBranches.average).map(branch => Object.values(branch)[0]))
    );

    const getFilteredBranches = (data) =>  {
      const allowedKeys = ['branch_1', 'branch_2', 'branch_3', 'branch_4'];
      return Object.fromEntries(
          Object.entries(data).filter(([key]) => allowedKeys.includes(key))
      );
    }

    const width = ref(window.innerWidth);

    const updateWidth = () => {
      width.value = window.innerWidth;
    };

    onMounted(() => {
      window.addEventListener('resize', updateWidth);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateWidth);
    });

    const isSmallScreen = computed(() => width.value <= 1024);

    const hasTotal = computed(() =>
        baseData.value.average?.total != null
    );

    const hasDomains = computed(() =>
        baseData.value.average?.domain_1 != null &&
        baseData.value.average?.domain_2 != null
    );

    const hasBranches = computed(() =>
        baseData.value.average?.branch_1 != null
    );

    const branches = ['branch_1', 'branch_2', 'branch_3', 'branch_4'];

    const sortedBranches = (input) => {
      return Object.keys(input)
          .sort((a, b) => {
            const numA = parseInt(a.split('_')[1]);
            const numB = parseInt(b.split('_')[1]);
            return numA - numB;
          })
          .reduce((sorted, key) => {
            sorted[key] = input[key];
            return sorted;
          }, {});
    };

    return {
      baseData,
      descriptions,
      barTotalData,
      barDomainsData,
      barDomaintDomains,
      branchBarStatistic,
      keysToLabels,
      dominateLabels,
      growLabels,
      dominatePieData,
      dominateBarData,
      growingPieData,
      levelDictionary,
      dictionary,
      growingBarData,
      dominateBarRespondentsData,
      barDataForAreas,
      checkScores,
      harmonicBarData,
      barRespondentsData,
      growingBarRespondentsData,
      getComputedData,
      print,
      getDifference,
      maxBranchValue,
      minBranchValue,
      getFilteredBranches,
      isSmallScreen,
      hasTotal,
      hasDomains,
      hasBranches,
      branches,
      sortedBranches
    };
  },
};
</script>

<style lang="scss" scoped>
.people-count {
  font-size: 24px;
  width: 70%;
  margin: auto;
  padding-left: 22px;

  &__element {
    i {
      margin-right: 7px;
    }
  }

  &__element:first-child {
    padding-left: 20px;
  }

  @media (max-width: 900px) {
    width: 100%;
  }
}

.report-chart-arrows .arrow:first-child {
  flex-basis: 100% !important;
}

.items-center {
  align-items: center;
}

.large-chart-wrapper {
  width: 65%;
  margin-left: -350px;
}

.small-charts-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  width: 35%;
  margin-left: -600px;
  margin-top: 50px;
}

.small-chart-block {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: -40px;
}

.small-chart-block:first-child {
  margin-top: -5px;
}

.small-chart-block.second-block {
  margin-top: -15px;
}

.chart-description {
  font-size: 13px;
  margin-left: -150px;
  margin-bottom: 60px;
  width: 50%;
}

.small-chart-block:first-child .chart-description {
  margin-bottom: 70px;
}

.scale {
  padding: 0 10px;
}


@media (max-width: 2400px) {
  .large-chart-wrapper {
    margin-left: -300px;
  }

  .small-charts-wrapper {
    margin-left: -500px;
  }
}

@media (max-width: 2200px) {
  .large-chart-wrapper {
    margin-left: -200px;
  }

  .small-charts-wrapper {
    margin-left: -400px;
  }
}

@media (max-width: 1920px) {
  .large-chart-wrapper {
    width: 60%;
    margin-left: -200px;
  }

  .small-charts-wrapper {
    width: 40%;
    margin-left: -200px;
    margin-top: 90px;
  }

  .chart-description {
    width: 50%;
    margin-left: -120px;
  }
}

@media (max-width: 1440px) {
  .large-chart-wrapper {
    width: 40%;
    margin-left: 0;
  }

  .small-charts-wrapper {
    width: 60%;
    margin-left: -50px;
    margin-top: 70px;
  }

  .chart-description {
    width: 50%;
    margin-left: -100px;
  }
}

@media (max-width: 1100px) {
  .large-chart-wrapper {
    width: 30%;
    margin-left: 0;
  }

  .small-charts-wrapper {
    width: 70%;
    margin-left: -50px;
  }

  .chart-description {
    width: 30%;
    margin-left: -100px;
  }
}

@media (max-width: 1024px) {
  .large-chart-wrapper {
    width: 100%;
    margin: 0;
  }

  .small-charts-wrapper {
    width: 100%;
    margin: 0;
  }

  .small-chart-block {
    flex-direction: column;
    align-items: flex-start;
  }

  .chart-description {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .chart-description {
    margin-top: 10px;
    font-size: 12px;
  }

  .download-report {
    width: 100%;
  }
}

</style>
